/**
 * 词汇学习钩子
 * 提供词汇列表数据访问和管理功能
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { useHistoryContext } from '../../history/contexts/index.js';
import { useLogger } from '../../logging/hooks/index.js';
/**
 * 词汇学习钩子，管理词汇列表数据
 *
 * @param options 配置选项
 * @returns 词汇列表数据和操作方法
 *
 * @example
 * ```tsx
 * const {
 *   items, loading, error, hasMore, stats,
 *   loadMore, refreshItems, deleteItem
 * } = useVocabulary({
 *   searchText: 'apple',
 *   sortBy: 'time-desc'
 * });
 * ```
 */
export function useVocabulary({ searchText = '', sortBy = 'time-desc', limit = 20, page: initialPage = 1, } = {}) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(initialPage);
    const [hasMore, setHasMore] = useState(false);
    const [stats, setStats] = useState({ totalCount: 0 });
    // 使用ref记录上次刷新统计的时间，避免频繁刷新
    const lastStatsRefreshTime = useRef(0);
    // 存储当前选项的引用，避免依赖数组变化
    const optionsRef = useRef({ searchText, sortBy, limit });
    // 存储页码的ref，避免依赖循环
    const pageRef = useRef(page);
    // 更新refs以跟踪最新值
    useEffect(() => {
        optionsRef.current = { searchText, sortBy, limit };
    }, [searchText, sortBy, limit]);
    useEffect(() => {
        pageRef.current = page;
    }, [page]);
    // 获取历史上下文
    const historyContext = useHistoryContext();
    const logger = useLogger();
    // 加载词汇数据 - 移除依赖数组中的动态值，使用ref代替
    const loadVocabulary = useCallback(async (isNewSearch = false) => {
        // 从ref中获取最新值，而不是从闭包
        const currentOptions = optionsRef.current;
        const currentPage = isNewSearch ? 1 : pageRef.current;
        // 如果是新搜索，重置页码
        if (isNewSearch && pageRef.current !== 1) {
            setPage(1);
            return; // 页码改变会触发useEffect重新加载
        }
        setLoading(true);
        setError(null);
        try {
            // 计算分页偏移量
            const offset = (currentPage - 1) * currentOptions.limit;
            // 根据是否有搜索文本，调用不同方法
            let result;
            if (currentOptions.searchText) {
                result = await historyContext.searchVocabulary(currentOptions.searchText, {
                    sortBy: currentOptions.sortBy,
                    limit: currentOptions.limit,
                    offset,
                });
            }
            else {
                result = await historyContext.getAllVocabulary({
                    sortBy: currentOptions.sortBy,
                    limit: currentOptions.limit,
                    offset,
                    searchText: undefined,
                });
            }
            // 限制刷新统计信息的频率，避免无限循环
            const now = Date.now();
            const timeSinceLastRefresh = now - lastStatsRefreshTime.current;
            // 如果上次刷新是在2秒之前，才进行刷新
            if (timeSinceLastRefresh > 2000) {
                await historyContext.refreshStats();
                lastStatsRefreshTime.current = now;
                // 从上下文获取最新统计
                const historyState = historyContext.historyState;
                setStats({
                    totalCount: historyState.totalVocabularyCount,
                });
            }
            // 如果是首页，直接设置数据
            if (currentPage === 1) {
                setItems(result);
            }
            else {
                // 否则追加数据
                setItems(prev => [...prev, ...result]);
            }
            // 判断是否还有更多数据
            setHasMore(result.length >= currentOptions.limit);
        }
        catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Unknown error';
            logger.error('Failed to load vocabulary data', { error: errorMessage });
            setError('Unable to load vocabulary data');
        }
        finally {
            setLoading(false);
        }
    }, [historyContext, logger] // 移除不稳定的依赖项，改用ref
    );
    // 使用ref存储loadVocabulary的最新版本
    const loadVocabularyRef = useRef(loadVocabulary);
    // 在渲染后更新ref值
    useEffect(() => {
        loadVocabularyRef.current = loadVocabulary;
    }, [loadVocabulary]);
    // 搜索或排序变化时重新加载 - 不再依赖loadVocabulary
    useEffect(() => {
        // 使用ref中的函数，而不是依赖项中的函数
        loadVocabularyRef.current(true);
    }, [searchText, sortBy]); // 移除loadVocabulary依赖
    // 页码变化时加载更多 - 不再依赖loadVocabulary
    useEffect(() => {
        // 初始页不触发加载
        if (page !== initialPage) {
            // 使用ref中的函数，而不是依赖项中的函数
            loadVocabularyRef.current();
        }
    }, [page, initialPage]); // 移除loadVocabulary依赖
    // 首次渲染时加载数据
    useEffect(() => {
        loadVocabularyRef.current(true);
        // 空依赖，只在组件挂载时执行一次
    }, []);
    // 加载更多
    const loadMore = useCallback(() => {
        setPage(prev => prev + 1);
    }, []);
    // 刷新数据
    const refreshItems = useCallback(() => {
        loadVocabularyRef.current(true);
    }, []);
    // 处理删除
    const deleteItem = useCallback(async (id) => {
        try {
            const success = await historyContext.deleteVocabularyItem(id);
            if (success) {
                // 更新列表，移除已删除项
                setItems(prev => prev.filter(item => item.orderId !== id));
                // 限制刷新统计信息的频率，避免无限循环
                const now = Date.now();
                const timeSinceLastRefresh = now - lastStatsRefreshTime.current;
                // 如果上次刷新是在2秒之前，才进行刷新
                if (timeSinceLastRefresh > 2000) {
                    await historyContext.refreshStats();
                    lastStatsRefreshTime.current = now;
                    // 更新统计状态
                    const historyState = historyContext.historyState;
                    setStats({
                        totalCount: historyState.totalVocabularyCount,
                    });
                }
                return true;
            }
            return false;
        }
        catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Unknown error';
            logger.error('Failed to delete vocabulary item', { error: errorMessage });
            return false;
        }
    }, [historyContext, logger]);
    return {
        items,
        loading,
        error,
        hasMore,
        stats,
        loadMore,
        refreshItems,
        deleteItem,
    };
}
//# sourceMappingURL=useVocabulary.js.map